// src/hooks/useWaiverClaimFlow.js
//
// DD-WAIVER-0723-BUILD W2 — shared waiver-claim launcher (Rule 11 reuse).
//
// Wraps the modal state + capacity computation + submit call that the
// FA-search surfaces already implement (VirtualPlayerTable.js /
// MobileFreeAgentSearch.js), so the other add surfaces don't re-derive it.
// The hook is presentation-free: desktop surfaces spread `modalProps` onto
// <DropDesignationModal>, mobile surfaces onto <MobileDropDesignationModal>
// (Bible § 14.0 mobile/desktop separation — the hook lives in the shared
// hooks layer, the component choice stays with the surface).
//
// Submit payload matches the FA-search simple-claim shape exactly
// (Waivers.js / VirtualPlayerTable.js handleWaiverClaimConfirm):
//   { player_league_id, drop_player_league_id }
// Plan-fingerprint claims (Phase A.4.5) are out of scope here.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { leaguesAPI } from '../services/apiService';
import { analyzeRosterCapacity, canClaimWaiverPlayer } from '../utils/RosterCapacityUtils';

// Capacity counts only players actually occupying a roster slot — matches
// usePlayerAssignment / useMobilePlayerAssignment loadRosterCapacity.
const filterCapacityPlayers = (players) =>
  (players || []).filter((p) => {
    const status = p.league_context?.roster?.status;
    return status && status !== 'former' && status !== 'inactive';
  });

/**
 * @param {object}   opts
 * @param {string}   opts.leagueId
 * @param {object}   [opts.league]           canonical league (§5 nested shape) —
 *                                           enables internal capacity analysis
 * @param {*}        [opts.myTeamId]         viewing team's id when the surface
 *                                           already knows it (commissioner mode:
 *                                           the acting team's id)
 * @param {boolean}  [opts.fetchMyTeamId]    when true and no myTeamId given,
 *                                           resolve the viewer's team via
 *                                           getUserTeam (one fetch, cached)
 * @param {object}   [opts.capacityAnalysis] reactive analysis from the surface's
 *                                           existing assignment infrastructure
 * @param {Array}    [opts.currentRoster]    reactive roster for the drop picker
 * @param {Function} [opts.onSuccess]        (player, message) after a claim lands
 * @param {Function} [opts.onError]          (message) on any failure
 */
export const useWaiverClaimFlow = ({
  leagueId,
  league = null,
  myTeamId = null,
  fetchMyTeamId = false,
  capacityAnalysis = null,
  currentRoster = null,
  onSuccess = null,
  onError = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingPlayer, setPendingPlayer] = useState(null);
  // { roster, analysis } captured at openClaim time (overrides / lazy fetch).
  const [snapshot, setSnapshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [fetchedTeamId, setFetchedTeamId] = useState(null);

  // Lazily resolve the viewer's team id for cooling-off checks on surfaces
  // that don't already have it (mobile pages without a userTeam prop).
  useEffect(() => {
    if (!fetchMyTeamId || myTeamId != null || fetchedTeamId != null || !leagueId) {
      return undefined;
    }
    let cancelled = false;
    leaguesAPI.getUserTeam(leagueId)
      .then((res) => {
        if (!cancelled && res?.success && res.team_id) setFetchedTeamId(res.team_id);
      })
      .catch((err) => {
        // Non-fatal: without a team id, cooling-off simply can't be
        // established client-side; the backend still enforces it.
        console.error('useWaiverClaimFlow: could not resolve viewer team id:', err);
      });
    return () => { cancelled = true; };
  }, [fetchMyTeamId, myTeamId, fetchedTeamId, leagueId]);

  const effectiveMyTeamId = myTeamId ?? fetchedTeamId;

  /**
   * Open the claim modal for an on-waivers player.
   * overrides.currentRoster / overrides.capacityAnalysis let surfaces that
   * just loaded fresh data hand it over directly (state written in the same
   * tick is not yet readable through the reactive args).
   */
  const openClaim = useCallback(async (player, overrides = {}) => {
    let roster = overrides.currentRoster ?? null;
    const analysis = overrides.capacityAnalysis ?? null;
    if (roster == null && currentRoster == null && leagueId) {
      // Surface has no roster infrastructure — load the viewer's canonical
      // roster for the drop picker (matches the FA-add pattern).
      try {
        const res = await leaguesAPI.getMyRosterCanonical(leagueId);
        if (res?.success && res.players) roster = res.players;
      } catch (err) {
        // Fail-closed: modal opens with an empty picker + isRosterFull=true;
        // the user can cancel — a plain ADD is never offered.
        console.error('useWaiverClaimFlow: failed to load roster for claim picker:', err);
      }
    }
    setSnapshot({ roster, analysis });
    setPendingPlayer(player);
    setIsOpen(true);
  }, [leagueId, currentRoster]);

  const closeClaim = useCallback(() => {
    setIsOpen(false);
    setPendingPlayer(null);
    setSnapshot(null);
  }, []);

  const confirmClaim = useCallback(async (dropPlayerId) => {
    if (!pendingPlayer) return false;
    setIsOpen(false);
    const leaguePlayerId = pendingPlayer.ids?.league_player;
    if (!leaguePlayerId) {
      if (onError) onError('Cannot claim: player has no league_player id.');
      setPendingPlayer(null);
      setSnapshot(null);
      return false;
    }
    setSubmitting(true);
    try {
      const res = await leaguesAPI.submitWaiverClaim(leagueId, {
        player_league_id: leaguePlayerId,
        drop_player_league_id: dropPlayerId || null,
      });
      const payload = res?.data || res;
      if (payload?.success) {
        const playerName = pendingPlayer.info?.full_name
          || `${pendingPlayer.info?.first_name || ''} ${pendingPlayer.info?.last_name || ''}`.trim()
          || 'player';
        if (onSuccess) onSuccess(pendingPlayer, `Waiver claim submitted for ${playerName}.`);
        return true;
      }
      if (onError) onError(payload?.message || payload?.error || 'Failed to submit waiver claim.');
      return false;
    } catch (err) {
      if (onError) {
        onError(
          err.response?.data?.message
          || err.response?.data?.error
          || err.message
          || 'Failed to submit waiver claim.'
        );
      }
      return false;
    } finally {
      setSubmitting(false);
      setPendingPlayer(null);
      setSnapshot(null);
    }
  }, [leagueId, pendingPlayer, onSuccess, onError]);

  const effectiveRoster = snapshot?.roster ?? currentRoster ?? [];

  // Analysis precedence: openClaim override > reactive arg > derived from
  // league + roster. Null analysis → isRosterFull=true (fail-closed, same
  // as the FA-search modal when capacity is unavailable).
  const effectiveAnalysis = useMemo(() => {
    if (snapshot?.analysis) return snapshot.analysis;
    if (capacityAnalysis) return capacityAnalysis;
    if (league) {
      const roster = snapshot?.roster ?? currentRoster;
      if (roster) return analyzeRosterCapacity(league, filterCapacityPlayers(roster));
    }
    return null;
  }, [snapshot, capacityAnalysis, league, currentRoster]);

  const claim = pendingPlayer && effectiveAnalysis && !effectiveAnalysis.error
    ? canClaimWaiverPlayer(pendingPlayer, effectiveAnalysis)
    : null;

  // Spread onto DropDesignationModal (desktop) / MobileDropDesignationModal
  // (mobile) — both share this prop contract.
  const modalProps = {
    isOpen,
    onClose: closeClaim,
    onConfirm: confirmClaim,
    playerBeingAdded: pendingPlayer,
    currentRoster: effectiveRoster,
    leagueId,
    context: 'waiver_claim',
    isRosterFull: claim ? !claim.canClaim : true,
    hasActiveSlot: claim ? claim.hasActiveSlot : null,
    hasBenchSlot: claim ? claim.hasBenchSlot : null,
    // Waiver priority is a league-level fact, not on the player object —
    // FA-search parity (VirtualPlayerTable.js:488) also passes null here.
    waiverPriority: null,
    totalTeams: null,
    waiverExpiresAt: pendingPlayer?.league_context?.availability?.waiver_expires_at || null,
  };

  return {
    openClaim,
    closeClaim,
    confirmClaim,
    isOpen,
    pendingPlayer,
    submitting,
    myTeamId: effectiveMyTeamId,
    modalProps,
  };
};

export default useWaiverClaimFlow;
